/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { useCallback, useRef, useState } from "react";
import EstimateCostDatabaseItems from "./EstimateCostDatabaseItemsGridNew";
import { itemListSources } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";

type CurrencyData = EstimateCurrency;

/**
 * CostDatabase/estimate item rows shown in this picker. Originated from EstimateItems aggregation
 * (server `getEstimateItemsResponse.result` is typed as `array(record(string, unknown))`).
 * The frontend only reads/writes `level` here; other fields flow through unchanged.
 */
type CostDatabasePickerItem = {
  level?: number;
  __isDummy?: boolean;
  // EstimateItem-shaped rows expose `parentId` as `string | null | undefined`.
  parentId?: string | null;
  targetLevel?: number;
  [key: string]: unknown;
};

type TreeGridRef = {
  dataView: {
    getAllSelectedItems: () => CostDatabasePickerItem[];
  };
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (row: number) => CostDatabasePickerItem;
  };
};

type CostDatabasePopupProps = {
  estimateId: string;
  handleDiscardItemAdditionFromPopup: () => void;
  handleItemAdditionFromPopup: (items: CostDatabasePickerItem[]) => void;
  parentId: string;
  currencyData: CurrencyData;
  level: number;
  buttonLoadingState?: boolean;
};

function CostDatabasePopup(props: CostDatabasePopupProps) {
  const { t } = useTranslation();
  const {
    estimateId,
    handleDiscardItemAdditionFromPopup,
    handleItemAdditionFromPopup,
    parentId,
    currencyData,
    level,
    buttonLoadingState,
  } = props;

  const initialTreeGridRef = useRef<TreeGridRef | null>(null);

  const getValidItemsQuery = {
    estimateParentId: parentId,
    estimateId,
  };

  const [isItemSelected, setIsItemSelected] = useState(false);

  function handleOnSelect() {
    const selectedItems =
      initialTreeGridRef.current?.slickGrid
        ?.getSelectedRows()
        ?.map((data) => initialTreeGridRef.current!.slickGrid.getDataItem(data))
        .filter(Boolean) ?? [];

    const selectedItemsUpdated = selectedItems.map((item) => ({
      ...item,
      parentId,
      targetLevel: level,
    }));
    selectedItemsUpdated.sort((a, b) => {
      const levelA = typeof a.level === "number" ? a.level : 0;
      const levelB = typeof b.level === "number" ? b.level : 0;
      return levelB - levelA;
    });

    handleItemAdditionFromPopup(selectedItemsUpdated);
  }

  function itemSelectionCallback() {
    const selectedItems =
      initialTreeGridRef.current?.dataView.getAllSelectedItems().filter((record) => record && !record.__isDummy) ?? [];
    setIsItemSelected(!!selectedItems.length);
  }

  const content = useCallback(() => {
    // Pre-existing: `allowCheckboxSelection` is consumed via legacy prop spread inside
    // EstimateCostDatabaseItems but is not declared on its Props type; cast through `unknown`
    // until the prop is added. The `initialTreeGridRef` cast bridges duplicate
    // `@types/react` resolutions in this workspace.
    const CostDatabaseItems = EstimateCostDatabaseItems as unknown as React.ComponentType<{
      allowCheckboxSelection?: boolean;
      isStaticData?: boolean;
      initialTreeGridRef?: typeof initialTreeGridRef;
      source: string;
      queryProp: { estimateParentId: string; estimateId: string };
      itemSelectionCallback: () => void;
      currencyData: typeof currencyData;
      enableSelectAll?: boolean;
    }>;
    return (
      <CostDatabaseItems
        allowCheckboxSelection
        isStaticData={true}
        initialTreeGridRef={initialTreeGridRef}
        source={itemListSources.ADD_ITEM_POPUP}
        queryProp={getValidItemsQuery}
        itemSelectionCallback={itemSelectionCallback}
        currencyData={currencyData}
        enableSelectAll={false}
      />
    );
  }, []);

  function footer() {
    return (
      <div className="">
        <ButtonComponent
          disabled={!isItemSelected}
          onClick={handleOnSelect}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={buttonLoadingState}
          className="me-4"
        >
          {t("Select")}
        </ButtonComponent>
        <ButtonComponent
          onClick={handleDiscardItemAdditionFromPopup}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          disabled={buttonLoadingState}
        >
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
  }

  return (
    <ModalStateDialogComponent width="75rem" height="85%" modelContent={content()} footerContent={footer()} isVisible />
  );
}

export default CostDatabasePopup;
